import { z } from 'zod';

import { PagedRequestSchema, PagedResultSchema } from './pagingSchema.ts';

export const AssetTypeSchema = z.string();
export const AssetTypeSchemaList = z.array(AssetTypeSchema);

export const AssetCapabilitiesSchema = z.string();
export const AssetCapabilitiesListSchema = z
  .array(AssetCapabilitiesSchema)
  .optional();

export const AssetSearchRequestSchema = z
  .object({
    searchText: z.coerce.string<string>().optional(),
  })
  .extend(PagedRequestSchema.shape);

export const BaseAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  typeId: z.string().min(1),
  assetTypeName: z.string().optional(),
  reference: z.string().optional(),
  capabilities: AssetCapabilitiesListSchema.optional(),
  skills: z.array(z.string().optional()).optional(),
  status: z.string().optional().nullable(),
  lastModified: z.date().optional().or(z.string()).nullable(),
  isActive: z.boolean().optional(),
  defaultDays: z.array(z.string()).optional(),
});

export const CreateAssetSchema = BaseAssetSchema.omit({
  id: true,
  capabilities: true,
  status: true,
  lastModified: true,
  name: true,
  reference: true,
}).extend({
  name: z.string().min(1),
  reference: z.string().optional(),
  defaultDays: z.array(z.string()).optional(),
});

export const CreateRampAssetSchema = BaseAssetSchema.omit({
  id: true,
  capabilities: true,
  status: true,
  lastModified: true,
  name: true,
  reference: true,
}).extend({
  name: z.string().min(1),
  reference: z.string().optional(),
  isDefault: z.boolean().optional(),
  defaultDays: z.array(z.string()).optional(),
});

export const UpdateAssetSchema = BaseAssetSchema.omit({
  capabilities: true,
  status: true,
  lastModified: true,
  name: true,
  reference: true,
}).extend({
  name: z.string().min(1),
  reference: z.string().optional(),
});

export const UpdateRampAssetSchema = BaseAssetSchema.omit({
  capabilities: true,
  status: true,
  lastModified: true,
  name: true,
  reference: true,
}).extend({
  name: z.string().min(1),
  reference: z.string().optional(),
  isDefault: z.boolean().optional(),
  defaultDays: z.array(z.string()).optional(),
});

export const AssetListSchema = z.array(BaseAssetSchema);

export const AssetSearchResultsSchema = PagedResultSchema.extend({
  results: AssetListSchema,
});

export const AssetReferenceTypesResponseSchema = z.object({
  types: z.array(z.object({ name: z.string() })),
});

export const AssetTypeObjectSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().optional(),
  serviceType: z.string().optional(),
});

export const AssetTypesListSchema = z.array(AssetTypeObjectSchema);

export const AssetTypesSearchLength = z
  .object({
    length: z.number().optional(),
  })
  .optional();

export const AssetTypesResponseSchema = PagedResultSchema.extend({
  results: AssetTypesListSchema,
});

export type AssetModel = z.infer<typeof BaseAssetSchema>;
export type AssetSearchRequestModel = z.infer<typeof AssetSearchRequestSchema>;
export type AssetListModel = z.infer<typeof AssetListSchema>;

export type AssetReferenceTypesResponseModel = z.infer<
  typeof AssetReferenceTypesResponseSchema
>;
export type AssetTypesListModel = z.infer<typeof AssetTypesListSchema>;
export type AssetTypesResponseModel = z.infer<typeof AssetTypesResponseSchema>;
export type AssetTypeObjectModel = z.infer<typeof AssetTypeObjectSchema>;
export type AssetSearchResultModel = z.infer<typeof AssetSearchResultsSchema>;
export type CreateAssetModel = z.infer<typeof CreateAssetSchema>;
export type CreateRampAssetModel = z.infer<typeof CreateRampAssetSchema>;
export type UpdateAssetModel = z.infer<typeof UpdateAssetSchema>;
export type UpdateRampAssetModel = z.infer<typeof UpdateRampAssetSchema>;
